import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import './Header.css';

function Header({ onShowLogin, onShowShopping, onShowMealPlan, onShowCollections, onHome, onShowEbookExport, onShowSwipe, onShowAbout }) {
  const { user, isAdmin, isMember, signOut } = useAuth();
  const { language, switchLanguage } = useLanguage();
  const t = translations[language];
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const nav = (fn) => { fn(); setMenuOpen(false); };

  return (
    <header className={`header${scrolled ? ' header--scrolled' : ''}`} ref={menuRef}>
      <div className="header__inner">
        <div className="header__main">
          <div>
            <h1 className="header__title" onClick={onHome} role="link" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onHome()}>Solt Pantry</h1>
            <p className="header__subtitle">{t.subtitle}</p>
          </div>
          <div className="header__auth">
            {isAdmin && (
              <div className="header__nav-row">
                <button className="header__nav-link" onClick={onHome}>{t.home || 'Home'}</button>
                <span className="header__nav-sep">·</span>
                <button className="header__nav-link" onClick={onShowSwipe}>{language === 'hu' ? 'Felfedezés' : 'Discover'}</button>
                <span className="header__nav-sep">·</span>
                <button className="header__nav-link" onClick={onShowMealPlan}>{t.mealPlan}</button>
                <span className="header__nav-sep">·</span>
                <button className="header__nav-link" onClick={onShowShopping}>{t.shoppingList}</button>
                <span className="header__nav-sep">·</span>
                <button className="header__nav-link" onClick={onShowCollections}>{t.collectionsLink}</button>
                <span className="header__nav-sep">·</span>
                <button className="header__nav-link" onClick={onShowEbookExport}>{t.exportCookbook}</button>
                <span className="header__nav-sep">·</span>
                <button className="header__nav-link" onClick={onShowAbout}>{t.aboutLink || 'About'}</button>
              </div>
            )}

            {isMember && (
              <div className="header__nav-row">
                <button className="header__nav-link" onClick={onHome}>{t.home || 'Home'}</button>
                <span className="header__nav-sep">·</span>
                <button className="header__nav-link" onClick={onShowSwipe}>{language === 'hu' ? 'Felfedezés' : 'Discover'}</button>
                <span className="header__nav-sep">·</span>
                <button className="header__nav-link" onClick={onShowCollections}>{t.collectionsLink}</button>
                <span className="header__nav-sep">·</span>
                <button className="header__nav-link" onClick={onShowAbout}>{t.aboutLink || 'About'}</button>
              </div>
            )}

            {!isAdmin && !isMember && (
              <div className="header__nav-row">
                <button className="header__nav-link" onClick={onShowAbout}>{t.aboutLink || 'About'}</button>
              </div>
            )}

            <div className="header__auth-row">
              <button
                className={`header__hamburger${menuOpen ? ' header__hamburger--open' : ''}`}
                onClick={() => setMenuOpen(o => !o)}
                aria-label="Menu"
              >
                {menuOpen ? '✕' : '☰'}
              </button>
              <div className="header__lang-toggle">
                <button
                  className={`header__lang-btn${language === 'en' ? ' header__lang-btn--active' : ''}`}
                  onClick={() => switchLanguage('en')}
                  aria-label="English"
                >EN</button>
                <span className="header__lang-sep">|</span>
                <button
                  className={`header__lang-btn${language === 'hu' ? ' header__lang-btn--active' : ''}`}
                  onClick={() => switchLanguage('hu')}
                  aria-label="Magyar"
                >HU</button>
              </div>
              {user ? (
                <button className="header__auth-btn" onClick={signOut} title={t.signOut}>
                  {/* Sign out icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                </button>
              ) : (
                <button className="header__auth-btn" onClick={onShowLogin} title={t.signIn}>
                  {/* Sign in icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                    <polyline points="10 17 15 12 10 7"/>
                    <line x1="15" y1="12" x2="3" y2="12"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {isAdmin && menuOpen && (
          <nav className="header__mobile-nav">
            <button className="header__mobile-nav-link" onClick={() => nav(onHome)}>{t.home || 'Home'}</button>
            <button className="header__mobile-nav-link" onClick={() => nav(onShowSwipe)}>{language === 'hu' ? 'Felfedezés' : 'Discover'}</button>
            <button className="header__mobile-nav-link" onClick={() => nav(onShowMealPlan)}>{t.mealPlan}</button>
            <button className="header__mobile-nav-link" onClick={() => nav(onShowShopping)}>{t.shoppingList}</button>
            <button className="header__mobile-nav-link" onClick={() => nav(onShowCollections)}>{t.collectionsLink}</button>
            <button className="header__mobile-nav-link" onClick={() => nav(onShowEbookExport)}>{t.exportCookbook}</button>
            <button className="header__mobile-nav-link" onClick={() => nav(onShowAbout)}>{t.aboutLink || 'About'}</button>
          </nav>
        )}
        {isMember && menuOpen && (
          <nav className="header__mobile-nav">
            <button className="header__mobile-nav-link" onClick={() => nav(onHome)}>{t.home || 'Home'}</button>
            <button className="header__mobile-nav-link" onClick={() => nav(onShowSwipe)}>{language === 'hu' ? 'Felfedezés' : 'Discover'}</button>
            <button className="header__mobile-nav-link" onClick={() => nav(onShowCollections)}>{t.collectionsLink}</button>
            <button className="header__mobile-nav-link" onClick={() => nav(onShowAbout)}>{t.aboutLink || 'About'}</button>
          </nav>
        )}
        {!isAdmin && !isMember && menuOpen && (
          <nav className="header__mobile-nav">
            <button className="header__mobile-nav-link" onClick={() => nav(onShowAbout)}>{t.aboutLink || 'About'}</button>
          </nav>
        )}
      </div>
    </header>
  );
}

export default Header;
